import { SearchspringCollectionProvider } from 'libs/searchspring/fetchers/collection'

import clsx from 'clsx'
import { useEffect, useState } from 'preact/hooks'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useInfiniteScrollResults } from 'libs/searchspring/hooks/useInfiniteScrollResults'
import { InfiniteScrollNext, InfiniteScrollPrevious } from 'libs/searchspring/components/collection/InfiniteScroll'
import { FiltersProvider } from 'libs/searchspring/hooks/useFilters'
import { CollectionToolbar } from 'libs/searchspring/components/collection/CollectionToolbar'
import { CustomFilter } from 'libs/searchspring/components/collection/filters/CustomFilter'
import { ResultItem } from 'libs/searchspring/components/ResultCardWrapper'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'

const GRID_IMAGE_SIZES = '(min-width: 1828px) 25vw, (min-width: 768px) 33vw, 100vw'
const GRID_IMAGE_WIDTHS = [200, 320, 480, 640, 800, 1000, 1200, 1440]

function CollectionGrid({ customFilter }) {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const { allResults, gridChange, isLoading } = useInfiniteScrollResults()
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const { setScrollElement } = useFilterScroll()

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  useEffect(() => {
    if (!isLoading && allResults?.length > 0) {
      const timer = setTimeout(() => {
        setShowSkeleton(false)
      }, 150)

      return () => clearTimeout(timer)
    } else if (isLoading || firstLoad) {
      setShowSkeleton(true)
    }
  }, [isLoading, allResults?.length, firstLoad])

  useScrollCache([allResults, firstLoad])

  return (
    <div className="grow flex flex-col">
      <FiltersProvider>
        {customFilter && <CustomFilter filter={customFilter} />}
        <CollectionToolbar />
        <div className="grow flex flex-col md:grid md:grid-cols-full-min">
          <div className="grow flex flex-col">
            <div
              ref={(el) => { setScrollElement(el); setEntry(el) }}
              className={clsx('collection-grid grid grid-cols-2 md:grid-cols-4 [grid-auto-flow:row_dense] w-full transition-all', (isLoading && firstLoad) && 'min-h-screen-height')}
            >
              <InfiniteScrollPrevious />
              {showSkeleton && firstLoad ? (
                Array.from({ length: window.settings?.per_page || 12 }, (_, index) => (
                  <SkeletonCard key={`skeleton-${index}`} className="w-full" />
                ))
              ) : (
                allResults?.map((item, index) => {
                  return <ResultItem item={item} key={item.handle} className="wk-wishlist-button-target" dataIndex={index.toString()} eager={index === 0} imageSizes={GRID_IMAGE_SIZES} imageWidths={GRID_IMAGE_WIDTHS} />
                })
              )}
            </div>
            {allResults.length > 0 && !firstLoad && (
              <InfiniteScrollNext />
            )}
          </div>
        </div>
      </FiltersProvider>
      <x-back-to-top>Back to Top</x-back-to-top>
    </div>
  )
}

export function WrappedCollectionGrid({ customFilter, perPage }) {
  const resultsPerPage = perPage || window.settings?.per_page
  scrollHistoryManager.updateHistoryState()
  return (
    <SearchspringCollectionProvider handle={window.collection?.handle} resultsPerPage={resultsPerPage}>
      <CollectionGrid customFilter={customFilter} />
    </SearchspringCollectionProvider>
  )
}
